/* eslint-disable no-console */
/**
 * Helper functions for manipulating tooth SVG overlay elements
 */

import { ClassMap } from '../types/tooth.types'
import {
  CRACK_CLASS_MAP,
  FRACTURE_CLASS_MAP,
  INLAY_SURFACE_MAP,
  DISPLAY,
} from './overlay-constants'
import { getLayerElements, getVariantElementsWithin } from './svg-overlay-index'

const DATA_LAYER_SUB_SELECTOR = /\[data-layer-sub=["']([^"']+)["']\]/i
const DATA_LAYER_SELECTOR = /\[data-layer=["']([^"']+)["']\]/i
const SIMPLE_OVERLAY_KEY = /^[a-z0-9_-]+$/i

function extractOverlayToken(selector: string): string {
  const dataLayerSub = selector.match(DATA_LAYER_SUB_SELECTOR)
  if (dataLayerSub) return dataLayerSub[1]
  const dataLayer = selector.match(DATA_LAYER_SELECTOR)
  if (dataLayer) return dataLayer[1]
  return selector.replace(/^\./, '').replace(/-overlay$/, '')
}

function getOverlayCandidates(selector: string): string[] {
  const token = extractOverlayToken(selector)
  const normalized = token.replace(/-/g, '_')
  const hyphenated = token.replace(/_/g, '-')
  const aliases: Record<string, string[]> = {
    sealant: ['sealants'],
    apicoetomy: ['apicoectomy'],
    root_canal: ['root_canal'],
    partial_pulpotomy: ['pulpotomy'],
    retained_primary: ['retained_primary'],
    sinus_tract: ['sinus_tract'],
    crown: ['crown'],
    caries: ['caries'],
  }

  return Array.from(
    new Set([token, normalized, hyphenated, ...(aliases[normalized] || [])]),
  ).filter((key) => SIMPLE_OVERLAY_KEY.test(key))
}

function queryByDataAttribute(
  svg: SVGSVGElement,
  attribute: 'data-layer-sub' | 'data-layer',
  key: string,
): SVGElement[] {
  return getLayerElements(svg, attribute, key)
}

export function getOverlayElements(
  svg: SVGSVGElement,
  selector: string,
): SVGElement[] {
  const legacy = Array.from(svg.querySelectorAll<SVGElement>(selector))
  const keys = getOverlayCandidates(selector)
  const dataLayerSubMatches = keys.flatMap((key) =>
    queryByDataAttribute(svg, 'data-layer-sub', key),
  )
  const dataLayerMatches = keys.flatMap((key) =>
    queryByDataAttribute(svg, 'data-layer', key),
  )
  return [...legacy, ...dataLayerSubMatches, ...dataLayerMatches]
}

function getOverlayElement(
  svg: SVGSVGElement,
  selector: string,
): SVGElement | null {
  const matches = getOverlayElements(svg, selector)
  return matches.length > 0 ? matches[0] : null
}

function getVariantCandidates(value: string): string[] {
  const hyphenToUnderscore = value.replace(/-/g, '_')
  const hyphenRemoved = value.replace(/-/g, '')
  const underscoreToHyphen = value.replace(/_/g, '-')
  const variantAliases: Record<string, string[]> = {
    // Recurring-caries SVGs may use generic names instead of caries/filling names.
    occlusal: ['cusp', 'surface'],
    'buccal-cusp': ['cusp'],
    'palatal-cusp': ['cusp'],
    'lingual-cusp': ['cusp'],
    'buccal-surface': ['surface'],
    'palatal-surface': ['surface'],
    'lingual-surface': ['surface'],
    'cervical-buccal': ['cervicalbuccal'],
    'cervical-palatal': ['cervicalpalatal'],
    'cervical-lingual': ['cervicallingual'],
  }
  const surfaceAliases: Record<string, string> = {
    facial: 'buccal',
    labial: 'buccal',
  }
  const fractureAliases: Record<string, string[]> = {
    crown_occlusal_horizontal: ['occlusal_horizontal'],
    crown_vertical: ['crown_vertical'],
    vertical_root: ['root_vertical'],
    horizontal_root: ['root_horizontal'],
    mifp: ['mesio_buccal', 'mesio_palatal'],
    mifl: ['mesio_buccal', 'mesio_lingual'],
    difp: ['disto_buccal', 'disto_palatal'],
    difl: ['disto_buccal', 'disto_lingual'],
    mesio_palatal: ['mesio_lingual'],
    disto_palatal: ['disto_lingual'],
    mesio_lingual: ['mesio_palatal'],
    disto_lingual: ['disto_palatal'],
  }
  const aliasVariants = new Set<string>()
  const baseVariants = [value, hyphenToUnderscore, underscoreToHyphen]

  baseVariants.forEach((variant) => {
    Object.entries(surfaceAliases).forEach(([from, to]) => {
      if (variant.includes(from)) {
        aliasVariants.add(variant.replace(new RegExp(from, 'g'), to))
      }
      if (variant.includes(to)) {
        aliasVariants.add(variant.replace(new RegExp(to, 'g'), from))
      }
    })
  })

  baseVariants.forEach((variant) => {
    const normalizedVariant = variant.replace(/_/g, '-')
    ;(variantAliases[normalizedVariant] || []).forEach((alias) => {
      aliasVariants.add(alias)
      aliasVariants.add(alias.replace(/-/g, '_'))
    })
  })

  return Array.from(
    new Set([
      value,
      hyphenToUnderscore,
      hyphenRemoved,
      underscoreToHyphen,
      value.replace(/^[a-z-]+overlay[.-]/, ''),
      hyphenToUnderscore.replace(/^[a-z_]+overlay[._]/, ''),
      ...Array.from(aliasVariants),
      ...(fractureAliases[hyphenToUnderscore] || []),
    ]),
  )
}

function findVariantElement(
  overlay: SVGElement,
  className: string,
): SVGElement | null {
  const candidates = getVariantCandidates(className)
  for (const candidate of candidates) {
    const byClass = overlay.querySelector<SVGElement>(`.${candidate}`)
    if (byClass) {
      return byClass
    }
    const byVariant = getVariantElementsWithin(overlay).find(
      (element) => element.getAttribute('data-layer-variant') === candidate,
    )
    if (byVariant) {
      return byVariant
    }
  }
  return null
}

/**
 * Converts a string with underscores to a hyphenated CSS class name
 * @param str - String to normalize (e.g., 'left_support')
 * @returns Normalized class name (e.g., 'left-support')
 */
export function normalizeClassName(str: string): string {
  return str.replace(/_/g, '-')
}

/**
 * Gets the CSS class name for a fracture type
 * @param fractureType - The fracture type
 * @returns The corresponding CSS class name, or empty string if not found
 */
export function getFractureClassName(fractureType: string): string {
  return (
    FRACTURE_CLASS_MAP[fractureType as keyof typeof FRACTURE_CLASS_MAP] || ''
  )
}

export function getFractureVariantCandidates(fractureType: string): string[] {
  const fractureClassName = getFractureClassName(fractureType)
  if (!fractureClassName) {
    return []
  }
  return getVariantCandidates(fractureClassName)
}

/**
 * Gets the CSS/data-layer variant name for a crack type
 * @param crackType - The crack type
 * @returns The corresponding variant name, or empty string if not found
 */
export function getCrackClassName(crackType: string): string {
  return CRACK_CLASS_MAP[crackType as keyof typeof CRACK_CLASS_MAP] || ''
}

/**
 * Gets the inlay surface class name for a given surface type
 * @param surfaceType - The surface type (e.g., 'mesial', 'distal', 'occlusal')
 * @returns The corresponding inlay surface class name
 */
export function getInlaySurfaceClass(surfaceType: string): string {
  return INLAY_SURFACE_MAP[surfaceType] || `inlay-${surfaceType}`
}

/**
 * Toggles the display of an overlay element
 * @param element - The SVG element to toggle
 * @param show - Whether to show (true) or hide (false) the element
 */
export function toggleOverlay(element: SVGElement | null, show: boolean): void {
  if (element) {
    if (show) {
      element.classList.add('active')
    } else {
      element.classList.remove('active')
    }
    element.style.display = show ? DISPLAY.BLOCK : DISPLAY.NONE
  }
}

function getSeverityVariant(severity: string): string {
  if (severity === 'slight') return 'class1'
  if (severity === 'moderate') return 'class2'
  if (severity === 'severe') return 'class3'
  return severity
}

/**
 * Handles a simple overlay that only needs to be shown or hidden
 * @param svg - The root SVG element
 * @param selector - CSS selector for the overlay
 * @param condition - Whether to show the overlay
 */
export function handleSimpleOverlay(
  svg: SVGSVGElement,
  selector: string,
  condition: boolean | string | undefined,
): void {
  try {
    const overlays = getOverlayElements(svg, selector)
    overlays.forEach((overlay) => {
      toggleOverlay(overlay, Boolean(condition))
    })
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Failed to handle overlay ${selector}:`, error)
    }
  }
}

/**
 * Handles an overlay with child elements that need to be shown/hidden based on a value
 * @param svg - The root SVG element
 * @param overlaySelector - CSS selector for the parent overlay
 * @param childSelector - CSS selector for child elements to hide
 * @param value - The value to match against (will be converted to class name)
 * @param childClassPrefix - Prefix for child class names (e.g., 'level-' for furcation)
 * @param transformValue - Optional function to transform the value before creating class name
 */
export function handleChildOverlay(
  svg: SVGSVGElement,
  overlaySelector: string,
  childSelector: string,
  value: string | undefined,
  childClassPrefix: string = '',
  transformValue?: (val: string) => string,
): void {
  try {
    const overlay = getOverlayElement(svg, overlaySelector)
    if (!overlay) {
      return
    }

    // Hide all child elements first
    overlay.querySelectorAll(childSelector).forEach((el) => {
      ;(el as SVGElement).style.display = DISPLAY.NONE
    })

    if (value) {
      const transformedValue = transformValue ? transformValue(value) : value
      const className = childClassPrefix
        ? `${childClassPrefix}${transformedValue}`
        : transformedValue
      const childElement = findVariantElement(overlay, className)
      if (childElement) {
        childElement.style.display = DISPLAY.BLOCK
        toggleOverlay(overlay, true)
      }
    } else {
      toggleOverlay(overlay, false)
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Failed to handle child overlay ${overlaySelector}:`, error)
    }
  }
}

/**
 * Handles an overlay with child elements using a class map (for drifted, etc.)
 * @param svg - The root SVG element
 * @param overlaySelector - CSS selector for the parent overlay
 * @param childSelector - CSS selector for child elements to hide
 * @param codes - Array of codes to process
 * @param classMap - Map from codes to class names
 * @param childClassPrefix - Prefix for child class names (e.g., 'drifted-overlay.')
 */
export function handleChildOverlayWithMap(
  svg: SVGSVGElement,
  overlaySelector: string,
  childSelector: string,
  codes: string[],
  classMap: ClassMap,
  childClassPrefix: string = '',
): void {
  try {
    const overlay = getOverlayElement(svg, overlaySelector)
    if (!overlay) {
      return
    }

    // Hide all child elements first
    overlay.querySelectorAll(childSelector).forEach((el) => {
      ;(el as SVGElement).style.display = DISPLAY.NONE
    })

    if (codes.length > 0) {
      codes.forEach((code) => {
        const cls = classMap[code]
        if (!cls) return

        const classNames = Array.isArray(cls) ? cls : [cls]
        classNames.forEach((className) => {
          const fullClassName = childClassPrefix
            ? `${childClassPrefix}${className}`
            : className
          const childElement = findVariantElement(overlay, fullClassName)
          if (childElement) {
            childElement.style.display = DISPLAY.BLOCK
          }
        })
      })
      toggleOverlay(overlay, true)
    } else {
      toggleOverlay(overlay, false)
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `Failed to handle child overlay with map ${overlaySelector}:`,
        error,
      )
    }
  }
}

/**
 * Handles a surface-based overlay (caries, filling, inlay)
 * @param svg - The root SVG element
 * @param overlaySelector - CSS selector for the overlay
 * @param surfaceSelector - CSS selector for surface elements
 * @param codes - Array of codes to process
 * @param classMap - Map from codes to class names
 * @param surfaceClassPrefix - Prefix for surface class names (e.g., 'caries-surface.')
 * @param materialType - Optional material type to set as attribute
 * @param materialAttribute - Attribute name for material type (default: 'material-type')
 * @param dataAttribute - Optional data attribute name and value to set
 * @param surfaceClearBehavior - Defaults to global class-selector clear; use `overlay-variants` for overlays that only expose `data-layer-variant` nodes (e.g. recurring caries SVG group).
 * @param dimInactiveSurfaces - When true (default), reset cleared surfaces with opacity 0.5; when false, remove inline opacity (used for fillings so SVG/CSS control visibility without dimming).
 */
export function handleSurfaceOverlay(
  svg: SVGSVGElement,
  overlaySelector: string,
  surfaceSelector: string,
  codes: string[],
  classMap: ClassMap,
  surfaceClassPrefix: string,
  materialType?: string,
  materialAttribute: string = 'material-type',
  dataAttribute?: { name: string; value: string },
  surfaceClearBehavior:
    | 'class-selector'
    | 'overlay-variants' = 'class-selector',
  dimInactiveSurfaces: boolean = true,
): void {
  try {
    const applyInlineFillByMaterial = (
      element: SVGElement,
      currentMaterialType?: string,
    ): void => {
      if (!currentMaterialType) return
      const materialFillColors: Record<string, string> = {
        zirconia: '#cee899',
        pfm: '#6f8fa6',
        pfz: '#7fb3a8',
        metal: '#9aa3ab',
        ceramic: '#9ecfeb',
        composite: '#c2aef8',
        amalgam: '#9aa3ab',
        gold: '#d4af37',
        temporary: '#E7D9CB',
      }
      const fillColor = materialFillColors[currentMaterialType.toLowerCase()]
      if (!fillColor) return

      const applyFillAndStripOpacity = (target: SVGElement): void => {
        target.setAttribute('fill', fillColor)
        target.removeAttribute('fill-opacity')
      }

      if (
        element instanceof SVGPathElement ||
        element instanceof SVGUseElement ||
        element instanceof SVGRectElement ||
        element instanceof SVGPolygonElement ||
        element instanceof SVGEllipseElement
      ) {
        applyFillAndStripOpacity(element)
      }

      element
        .querySelectorAll<SVGElement>('use, path, rect, polygon, ellipse')
        .forEach((child) => {
          applyFillAndStripOpacity(child)
        })
    }

    const applyInlineFillByCariesType = (
      element: SVGElement,
      currentCariesType?: string,
    ): void => {
      if (!currentCariesType) return
      const cariesFillColors: Record<string, string> = {
        d1: '#ffa7a1',
        d2: '#fe746b',
        d3: '#d8352a',
        e1: '#65acf5',
        e2: '#185dd8',
      }
      const fillColor = cariesFillColors[currentCariesType.toLowerCase()]
      if (!fillColor) return

      if (
        element instanceof SVGPathElement ||
        element instanceof SVGUseElement ||
        element instanceof SVGRectElement ||
        element instanceof SVGPolygonElement ||
        element instanceof SVGEllipseElement
      ) {
        element.setAttribute('fill', fillColor)
      }

      element
        .querySelectorAll<SVGElement>('use, path, rect, polygon, ellipse')
        .forEach((child) => {
          child.setAttribute('fill', fillColor)
        })
    }

    // Crown/occlusal SVGs may include multiple matching overlay roots (e.g. recurring caries
    // under conditions and under data-layer="crown"); updating only the first leaves the rest
    // on their default visibility (often all surfaces shown).
    const overlays = getOverlayElements(svg, overlaySelector)

    overlays.forEach((overlay) => {
      toggleOverlay(overlay, false)
      // For new SVG structure, ensure all variants are hidden before activating requested surfaces.
      getVariantElementsWithin(overlay).forEach((el) => {
        el.style.display = DISPLAY.NONE
      })
    })

    const clearTargets =
      surfaceClearBehavior === 'overlay-variants'
        ? Array.from(
            new Set<SVGElement>([
              ...overlays.flatMap((overlay) =>
                getVariantElementsWithin(overlay),
              ),
              ...Array.from(svg.querySelectorAll<SVGElement>(surfaceSelector)),
            ]),
          )
        : Array.from(svg.querySelectorAll<SVGElement>(surfaceSelector))

    // Clear surface classes / attributes on relevant nodes
    clearTargets.forEach((el) => {
      el.classList.remove('active')
      el.removeAttribute(materialAttribute)
      if (dimInactiveSurfaces) {
        // Restore default inline opacity for inactive surfaces (caries, etc.)
        el.setAttribute('opacity', '0.5')
      } else {
        el.removeAttribute('opacity')
      }
      if (dataAttribute) {
        el.removeAttribute(dataAttribute.name)
      }
    })

    const expandedCodes = new Set(codes)

    if (overlays.length > 0 && expandedCodes.size > 0) {
      overlays.forEach((overlay) => toggleOverlay(overlay, true))
    }

    const activateSurfaceElement = (el: SVGElement): void => {
      el.classList.add('active')
      el.style.display = DISPLAY.BLOCK
      // Remove inline opacity attribute to allow CSS to control opacity
      el.removeAttribute('opacity')
      if (materialType) {
        el.setAttribute(materialAttribute, materialType)
        // New SVGs often render surfaces as <g data-layer-variant><use /></g>,
        // so set fill inline instead of relying only on class-based CSS selectors.
        applyInlineFillByMaterial(el, materialType)
      }
      if (dataAttribute) {
        el.setAttribute(dataAttribute.name, dataAttribute.value)
        if (dataAttribute.name === 'data-caries-type') {
          applyInlineFillByCariesType(el, dataAttribute.value)
        }
      }
    }

    expandedCodes.forEach((code) => {
      const cls = classMap[code]
      if (!cls) return

      const classNames = Array.isArray(cls) ? cls : [cls]
      classNames.forEach((className) => {
        const fullClassName = `${surfaceClassPrefix}${className}`
        const surfaceClassName = fullClassName.replace(/^[^.]*\./, '')
        const seenForSurface = new Set<SVGElement>()
        const visitSurface = (candidate: SVGElement): void => {
          if (seenForSurface.has(candidate)) return
          seenForSurface.add(candidate)
          activateSurfaceElement(candidate)
        }

        svg
          .querySelectorAll<SVGElement>(`.${fullClassName}`)
          .forEach((surfaceEl) => visitSurface(surfaceEl))

        overlays.forEach((overlay) => {
          const matched = findVariantElement(overlay, surfaceClassName)
          if (matched) {
            visitSurface(matched)
          }
        })
      })
    })
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `Failed to handle surface overlay ${overlaySelector}:`,
        error,
      )
    }
  }
}

/**
 * Handles an overlay with material type attributes on child elements
 * @param svg - The root SVG element
 * @param overlaySelector - CSS selector for the overlay
 * @param childSelector - CSS selector for child elements (e.g., 'path')
 * @param condition - Whether to show the overlay
 * @param materialType - Material type to set on child elements
 * @param materialAttribute - Attribute name for material type (default: 'material-type')
 */
export function handleMaterialOverlay(
  svg: SVGSVGElement,
  overlaySelector: string,
  childSelector: string,
  condition: boolean,
  materialType?: string,
  materialAttribute: string = 'material-type',
): void {
  try {
    const overlayClassName = overlaySelector.startsWith('.')
      ? overlaySelector.slice(1)
      : overlaySelector
    const overlays = getOverlayElements(svg, overlaySelector)
    overlays.forEach((overlay) => {
      // Ensure data-layer-sub overlays also match class-based CSS selectors.
      if (overlayClassName) {
        overlay.classList.add(overlayClassName)
      }
      // Clear material-type from child elements
      overlay.querySelectorAll(childSelector).forEach((child) => {
        child.removeAttribute(materialAttribute)
      })
      toggleOverlay(overlay, false)
    })

    if (condition) {
      const overlay = getOverlayElement(svg, overlaySelector)
      if (overlay) {
        toggleOverlay(overlay, true)
        if (materialType) {
          overlay.querySelectorAll(childSelector).forEach((child) => {
            child.setAttribute(materialAttribute, materialType)
          })
        }
      }
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `Failed to handle material overlay ${overlaySelector}:`,
        error,
      )
    }
  }
}

/**
 * Crown type to fill color mapping
 */
const CROWN_FILL_COLORS: Record<string, string> = {
  zirconia: '#cee899',
  pfm: '#6f8fa6',
  pfz: '#7fb3a8',
  metal: '#9aa3ab',
  ceramic: '#9ecfeb',
  composite: '#c2aef8',
  amalgam: '#9aa3ab',
  gold: '#d4af37',
  temporary: '#E7D9CB',
}

const MATERIAL_FILL_COLORS: Record<string, string> = CROWN_FILL_COLORS

function applyMaterialFillColor(
  element: SVGElement,
  materialType?: string,
): void {
  if (!materialType) return
  const fillColor = MATERIAL_FILL_COLORS[materialType.toLowerCase()]
  if (!fillColor) return

  // If target is a drawable element, apply directly.
  if (
    element instanceof SVGPathElement ||
    element instanceof SVGUseElement ||
    element instanceof SVGRectElement ||
    element instanceof SVGPolygonElement ||
    element instanceof SVGEllipseElement
  ) {
    element.setAttribute('fill', fillColor)
  }

  // Also apply to nested drawables (common case: <g data-layer-variant><use ... /></g>).
  element
    .querySelectorAll<SVGElement>('use, path, rect, polygon, ellipse')
    .forEach((child) => {
      child.setAttribute('fill', fillColor)
    })
}

function getCrownLayerRoot(svg: SVGSVGElement): SVGElement | null {
  const byDataLayer = getLayerElements(svg, 'data-layer', 'crown').find(
    (element) => element.tagName.toLowerCase() === 'g',
  )
  if (byDataLayer) {
    return byDataLayer
  }
  return getOverlayElement(svg, '.crown-overlay')
}

/**
 * Handles crown overlay by updating fill of drawable elements and toggling the crown layer.
 * Crown SVGs use an outer layer g[data-layer="crown"] for visibility (CSS + .active) and an
 * inner sub-layer for the shape (<use>/<path>, or legacy <rect>).
 *
 * @param svg - The root SVG element
 * @param crownType - The crown material type
 */
export function handleCrownOverlay(
  svg: SVGSVGElement,
  crownType: string | undefined,
): void {
  try {
    const crownLayer = getCrownLayerRoot(svg)
    if (!crownLayer) {
      return
    }

    const crownSubs = getLayerElements(svg, 'data-layer-sub', 'crown').filter(
      (element) =>
        element.tagName.toLowerCase() === 'g' && crownLayer.contains(element),
    )
    const crownShapeGroup =
      crownSubs.find((element) => element.parentNode === crownLayer) ??
      crownSubs[0] ??
      crownLayer

    const rectElements =
      crownShapeGroup.querySelectorAll<SVGRectElement>('rect')
    const useElements = crownShapeGroup.querySelectorAll<SVGUseElement>('use')
    const pathElements =
      crownShapeGroup.querySelectorAll<SVGPathElement>('path')
    const polygonElements =
      crownShapeGroup.querySelectorAll<SVGPolygonElement>('polygon')
    const ellipseElements =
      crownShapeGroup.querySelectorAll<SVGEllipseElement>('ellipse')

    const isCrownView = rectElements.length > 0
    const targetElements: SVGElement[] = isCrownView
      ? Array.from(rectElements)
      : useElements.length > 0
        ? Array.from(useElements)
        : pathElements.length > 0
          ? Array.from(pathElements)
          : [...Array.from(polygonElements), ...Array.from(ellipseElements)]

    if (crownType && CROWN_FILL_COLORS[crownType]) {
      crownLayer.setAttribute('material-type', crownType)
      crownShapeGroup.setAttribute('material-type', crownType)
      targetElements.forEach((el) => {
        applyMaterialFillColor(el, crownType)
      })
      toggleOverlay(crownLayer, true)
      toggleOverlay(crownShapeGroup, true)
    } else {
      crownLayer.removeAttribute('material-type')
      crownShapeGroup.removeAttribute('material-type')
      targetElements.forEach((el) => {
        el.removeAttribute('fill')
      })
      toggleOverlay(crownShapeGroup, false)
      toggleOverlay(crownLayer, false)
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to handle crown overlay:', error)
    }
  }
}

/**
 * Keeps the implant fixture in front of crown and abutment artwork. SVG paint
 * order follows DOM order, while source files do not all place these layers in
 * the same order.
 */
export function ensureImplantAboveProsthetic(
  svg: SVGSVGElement,
  crownActive: boolean,
  implantActive: boolean,
): void {
  const crownLayer = getCrownLayerRoot(svg)
  const implantLayer = getOverlayElement(svg, '.implant-overlay')
  if (!implantLayer) {
    return
  }

  if (!implantActive) {
    if (crownActive && crownLayer?.parentNode === svg) {
      svg.appendChild(crownLayer)
    }
    return
  }

  const structuralLayer = implantLayer.closest<SVGElement>(
    'g[data-layer="structural"]',
  )
  if (!structuralLayer) {
    implantLayer.parentNode?.appendChild(implantLayer)
    return
  }

  // Within the structural layer, the implant must paint after the pontic body
  // used by abutments. The structural layer itself must paint after the crown.
  structuralLayer.appendChild(implantLayer)
  if (structuralLayer.parentNode === svg) {
    svg.appendChild(structuralLayer)
  }
}

/**
 * Handles erosion overlay with multiple surface types
 * @param svg - The root SVG element
 * @param erosion - General erosion flag
 * @param erosionBuccal - Buccal erosion flag
 * @param erosionPalatal - Palatal erosion flag
 * @param erosionLingual - Lingual erosion flag
 * @param erosionOcclusal - Occlusal erosion flag
 */
export function handleErosionOverlay(
  svg: SVGSVGElement,
  erosion: boolean,
  erosionBuccal: boolean,
  erosionPalatal: boolean,
  erosionLingual: boolean,
  erosionOcclusal: boolean,
): void {
  try {
    const erosionOverlay = getOverlayElement(svg, '.erosion-overlay')
    if (erosionOverlay) {
      // Hide all child variants first for both old and new SVG structures.
      getVariantElementsWithin(erosionOverlay).forEach((el) => {
        el.style.display = DISPLAY.NONE
      })
      erosionOverlay
        .querySelectorAll(
          '.erosion-buccal, .erosion-palatal, .erosion-lingual, .erosion-occlusal',
        )
        .forEach((el) => {
          ;(el as SVGElement).style.display = DISPLAY.NONE
        })

      const activateVariant = (variant: string): void => {
        const variantEl = findVariantElement(erosionOverlay, variant)
        if (variantEl) {
          variantEl.style.display = DISPLAY.BLOCK
        }
        const classEl = erosionOverlay.querySelector<SVGElement>(
          `.erosion-${variant}`,
        )
        if (classEl) {
          classEl.style.display = DISPLAY.BLOCK
        }
      }

      if (erosionBuccal) activateVariant('buccal')
      if (erosionPalatal) activateVariant('palatal')
      if (erosionLingual) activateVariant('lingual')
      if (erosionOcclusal) activateVariant('occlusal')

      // Show parent overlay if any erosion surface is active (or legacy general erosion).
      toggleOverlay(
        erosionOverlay,
        erosion ||
          erosionBuccal ||
          erosionPalatal ||
          erosionLingual ||
          erosionOcclusal,
      )
    }

    // Handle legacy overlay classes for backwards compatibility
    handleSimpleOverlay(svg, '.erosion-buccal-overlay', erosionBuccal)
    handleSimpleOverlay(svg, '.erosion-palatal-overlay', erosionPalatal)
    handleSimpleOverlay(svg, '.erosion-lingual-overlay', erosionLingual)
    handleSimpleOverlay(svg, '.erosion-occlusal-overlay', erosionOcclusal)

    // Handle direct erosion elements (for components that don't use overlay structure)
    const buccalErosionElements =
      svg.querySelectorAll<SVGElement>('.erosion-buccal')
    const palatalErosionElements =
      svg.querySelectorAll<SVGElement>('.erosion-palatal')
    const lingualErosionElements =
      svg.querySelectorAll<SVGElement>('.erosion-lingual')
    const occlusalErosionElements =
      svg.querySelectorAll<SVGElement>('.erosion-occlusal')

    // Only apply to elements that are NOT inside an erosion-overlay
    buccalErosionElements.forEach((element) => {
      if (!element.closest('.erosion-overlay')) {
        toggleOverlay(element, erosionBuccal)
      }
    })

    palatalErosionElements.forEach((element) => {
      if (!element.closest('.erosion-overlay')) {
        toggleOverlay(element, erosionPalatal)
      }
    })

    lingualErosionElements.forEach((element) => {
      if (!element.closest('.erosion-overlay')) {
        toggleOverlay(element, erosionLingual)
      }
    })
    occlusalErosionElements.forEach((element) => {
      if (!element.closest('.erosion-overlay')) {
        toggleOverlay(element, erosionOcclusal)
      }
    })
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to handle erosion overlay:', error)
    }
  }
}

/**
 * Handles bridge overlay with type support and material fill colors
 * @param svg - The root SVG element
 * @param bridge - Bridge value (boolean or string type)
 * @param bridgeMaterial - Bridge material type (zirconia, pfm, metal, ceramic, composite)
 */
export function handleBridgeOverlay(
  svg: SVGSVGElement,
  bridge: boolean | string | undefined,
  bridgeMaterial?: string,
): void {
  try {
    const bridgeOverlay = getOverlayElement(svg, '.pontic-overlay')
    if (!bridgeOverlay) {
      return
    }

    // Hide all connector variants first (supports both legacy and new SVGs).
    bridgeOverlay
      .querySelectorAll(
        '.pontic-type, [data-layer-variant="mesial"], [data-layer-variant="distal"]',
      )
      .forEach((el) => {
        ;(el as SVGElement).style.display = DISPLAY.NONE
      })

    // Get material fill color if material is provided
    const normalizedBridgeMaterial = bridgeMaterial?.toLowerCase()

    // Helper function to apply fill to bridge base elements (the crown/body group after connector variants).
    const applyBaseFill = (): void => {
      const baseGroups = Array.from(bridgeOverlay.children).filter(
        (child) =>
          child instanceof SVGGElement &&
          !child.classList.contains('pontic-type') &&
          child.getAttribute('data-layer-variant') !== 'mesial' &&
          child.getAttribute('data-layer-variant') !== 'distal',
      ) as SVGGElement[]

      baseGroups.forEach((baseGroup) => {
        if (normalizedBridgeMaterial) {
          applyMaterialFillColor(baseGroup, bridgeMaterial)
        } else {
          baseGroup
            .querySelectorAll<SVGElement>('use, rect[mask], path')
            .forEach((el) => {
              el.removeAttribute('fill')
            })
        }
      })
    }

    if (bridge) {
      if (bridge === true) {
        // For boolean true, show the overlay but don't show any specific type
        toggleOverlay(bridgeOverlay, true)
        // Apply fill after overlay is shown
        applyBaseFill()
      } else {
        // Map bridge/abutment types to connector directions.
        const showMesial =
          bridge === 'left_bridge' || bridge === 'left_abutment'
        const showDistal =
          bridge === 'right_bridge' || bridge === 'right_abutment'
        const showBoth =
          bridge === 'right_left_bridge' || bridge === 'right_left_abutment'

        const setConnectorVisible = (connector: 'mesial' | 'distal'): void => {
          const connectorElement =
            findVariantElement(bridgeOverlay, connector) ||
            // Legacy class fallback
            bridgeOverlay.querySelector<SVGElement>(
              `.pontic-type.${connector === 'mesial' ? 'left-support' : 'right-support'}`,
            )

          if (!connectorElement) return
          connectorElement.style.display = DISPLAY.BLOCK
          applyMaterialFillColor(connectorElement, bridgeMaterial)
        }

        if (showBoth) {
          setConnectorVisible('mesial')
          setConnectorVisible('distal')
        } else {
          if (showMesial) {
            setConnectorVisible('mesial')
          } else if (showDistal) {
            setConnectorVisible('distal')
          } else {
            // Fallback for unknown string values.
            const cssClass = normalizeClassName(bridge)
            const bridgeTypeElement = bridgeOverlay.querySelector<SVGElement>(
              `.pontic-type.${cssClass}`,
            )
            if (bridgeTypeElement) {
              bridgeTypeElement.style.display = DISPLAY.BLOCK
              applyMaterialFillColor(bridgeTypeElement, bridgeMaterial)
            }
          }
        }
        toggleOverlay(bridgeOverlay, true)
        // Apply fill after overlay is shown
        applyBaseFill()
      }
    } else {
      // Reset fill colors when bridge is removed (including base crown path).
      bridgeOverlay
        .querySelectorAll<SVGElement>('use, rect[mask], path')
        .forEach((el) => {
          if (!el.closest('.pontic-type')) {
            el.removeAttribute('fill')
          }
        })
      // Also reset support rect fills
      bridgeOverlay.querySelectorAll('.pontic-type rect').forEach((el) => {
        ;(el as SVGElement).removeAttribute('fill')
      })
      toggleOverlay(bridgeOverlay, false)
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to handle bridge overlay:', error)
    }
  }
}

const setFurcationVariantVisible = (
  element: SVGElement,
  show: boolean,
): void => {
  if (show) {
    element.classList.add('active')
    element.style.display = DISPLAY.BLOCK
  } else {
    element.classList.remove('active')
    element.style.display = DISPLAY.NONE
  }
}

const parseFurcationAreaCodes = (
  furcationAreas: string | undefined,
): string[] | null => {
  // undefined = legacy "areas not specified" (show all).
  // '' or whitespace = user cleared all areas (show none).
  if (furcationAreas === undefined) {
    return null
  }
  return furcationAreas
    .split(',')
    .map((area) => area.trim())
    .filter(Boolean)
}

const getFurcationAreaCandidates = (areaCode: string): string[] => {
  const trimmed = areaCode.trim().toLowerCase()
  const hyphenToUnderscore = trimmed.replace(/-/g, '_')
  const normalizedDistal = hyphenToUnderscore.replace(
    /(^|_)distal(?=_|$)/g,
    '$1disto',
  )
  const tokens = normalizedDistal.split('_').filter(Boolean)
  const reversed =
    tokens.length === 2 ? `${tokens[1]}_${tokens[0]}` : normalizedDistal
  return Array.from(
    new Set([
      trimmed,
      hyphenToUnderscore,
      normalizedDistal,
      reversed,
      reversed.replace(/_/g, '-'),
    ]),
  )
}

/** Furcation overlay with level + optional area filtering. */
export function handleFurcationOverlay(
  svg: SVGSVGElement,
  furcation: string | undefined,
  furcationAreas?: string,
): void {
  try {
    const furcationOverlay = getOverlayElement(svg, '.furcation-overlay')
    if (!furcationOverlay) {
      return
    }

    furcationOverlay.querySelectorAll('.furcation-level').forEach((el) => {
      setFurcationVariantVisible(el as SVGElement, false)
    })

    furcationOverlay
      .querySelectorAll('[class*="furcation-area-"], [data-layer-variant]')
      .forEach((el) => {
        setFurcationVariantVisible(el as SVGElement, false)
      })

    if (!furcation) {
      toggleOverlay(furcationOverlay, false)
      return
    }

    const upperFurcation = furcation.toUpperCase()
    const levelNumber = upperFurcation.charAt(1)
    const selectedAreas = parseFurcationAreaCodes(furcationAreas)
    const levelElement = furcationOverlay.querySelector<SVGElement>(
      `.furcation-level.level-${levelNumber}`,
    )

    if (levelElement) {
      setFurcationVariantVisible(levelElement, true)
      if (selectedAreas === null) {
        levelElement
          .querySelectorAll('[class*="furcation-area-"], [data-layer-variant]')
          .forEach((el) => {
            setFurcationVariantVisible(el as SVGElement, true)
          })
      } else {
        selectedAreas.forEach((areaCode) => {
          const areaElement =
            levelElement.querySelector<SVGElement>(
              `.furcation-area-${areaCode}`,
            ) ||
            levelElement.querySelector<SVGElement>(
              `[data-layer-variant="${areaCode}_class${levelNumber}"]`,
            )
          if (areaElement) {
            setFurcationVariantVisible(areaElement, true)
          }
        })
      }
      toggleOverlay(furcationOverlay, true)
      return
    }

    const allVariants = getVariantElementsWithin(furcationOverlay)
    if (allVariants.length === 0) {
      toggleOverlay(furcationOverlay, false)
      return
    }

    const classSuffix = `class${levelNumber}`
    const areaCandidates =
      selectedAreas === null
        ? null
        : selectedAreas.flatMap((area) => getFurcationAreaCandidates(area))

    let visibleCount = 0
    allVariants.forEach((variant) => {
      const variantName = (
        variant.getAttribute('data-layer-variant') || ''
      ).toLowerCase()
      const matchesLevel = variantName.endsWith(`_${classSuffix}`)
      if (!matchesLevel) {
        return
      }
      if (areaCandidates === null) {
        setFurcationVariantVisible(variant, true)
        visibleCount += 1
        return
      }
      const variantArea = variantName.replace(
        new RegExp(`_${classSuffix}$`),
        '',
      )
      if (areaCandidates.includes(variantArea)) {
        setFurcationVariantVisible(variant, true)
        visibleCount += 1
      }
    })

    toggleOverlay(furcationOverlay, visibleCount > 0 || selectedAreas === null)
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to handle furcation overlay:', error)
    }
  }
}

/**
 * Handles inlay overlay with surface and material support
 * @param svg - The root SVG element
 * @param inlay - Array of inlay codes
 * @param inlayMaterial - Material type for inlays
 * @param inlayClassMap - Map from codes to class names
 */
export function handleInlayOverlay(
  svg: SVGSVGElement,
  inlay: string[],
  inlayMaterial: string | undefined,
  inlayClassMap: ClassMap,
): void {
  try {
    const inlayOverlay = getOverlayElement(svg, '.inlay-overlay')
    if (inlayOverlay) {
      toggleOverlay(inlayOverlay, false)
      getVariantElementsWithin(inlayOverlay).forEach((el) => {
        el.style.display = DISPLAY.NONE
      })
    }

    // Clear all inlay surface classes and material-type attributes
    svg.querySelectorAll('.inlay-surface').forEach((el) => {
      el.classList.remove('active')
      el.removeAttribute('material-type')
      ;(el as SVGElement).style.display = DISPLAY.NONE
    })

    // Show inlay overlay if there are inlay surfaces
    if (inlay.length > 0 && inlayOverlay) {
      toggleOverlay(inlayOverlay, true)
    }

    // Handle inlay surfaces with material-type attribute
    inlay.forEach((code) => {
      const cls = inlayClassMap[code]
      if (!cls) return

      const classNames = Array.isArray(cls) ? cls : [cls]
      classNames.forEach((className) => {
        const inlaySurfaceClass = getInlaySurfaceClass(className)
        const el =
          svg.querySelector<SVGElement>(
            `.inlay-surface.${inlaySurfaceClass}`,
          ) ||
          (inlayOverlay
            ? findVariantElement(
                inlayOverlay,
                inlaySurfaceClass.replace(/^inlay-/, ''),
              )
            : null)
        if (el) {
          el.classList.add('active')
          el.style.display = DISPLAY.BLOCK
          if (inlayMaterial) {
            el.setAttribute('material-type', inlayMaterial)
            applyMaterialFillColor(el, inlayMaterial)
          }
        }
      })
    })
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to handle inlay overlay:', error)
    }
  }
}

/**
 * Handles onlay overlay with surface and material support
 * @param svg - The root SVG element
 * @param onlay - Array of onlay codes
 * @param onlayMaterial - Material type for onlays
 * @param onlayClassMap - Map from codes to class names
 */
export function handleOnlayOverlay(
  svg: SVGSVGElement,
  onlay: string[],
  onlayMaterial: string | undefined,
  onlayClassMap: ClassMap,
): void {
  try {
    const onlayOverlay = getOverlayElement(svg, '.onlay-overlay')
    if (onlayOverlay) {
      toggleOverlay(onlayOverlay, false)
      getVariantElementsWithin(onlayOverlay).forEach((el) => {
        el.style.display = DISPLAY.NONE
      })
    }

    // Clear all onlay surface classes and material-type attributes
    svg.querySelectorAll('.onlay-surface').forEach((el) => {
      el.classList.remove('active')
      el.removeAttribute('material-type')
      ;(el as SVGElement).style.display = DISPLAY.NONE
    })

    // Show onlay overlay only when there are selected surfaces
    if (onlay.length > 0 && onlayOverlay) {
      toggleOverlay(onlayOverlay, true)
    }

    // Activate selected onlay surfaces and apply material
    onlay.forEach((code) => {
      const cls = onlayClassMap[code]
      if (!cls) return

      const classNames = Array.isArray(cls) ? cls : [cls]
      classNames.forEach((className) => {
        const onlaySurfaceClass = `onlay-${className}`
        const el =
          svg.querySelector<SVGElement>(
            `.onlay-surface.${onlaySurfaceClass}`,
          ) ||
          (onlayOverlay
            ? findVariantElement(
                onlayOverlay,
                onlaySurfaceClass.replace(/^onlay-/, ''),
              )
            : null)
        if (el) {
          el.classList.add('active')
          el.style.display = DISPLAY.BLOCK
          if (onlayMaterial) {
            el.setAttribute('material-type', onlayMaterial)
            applyMaterialFillColor(el, onlayMaterial)
          }
        }
      })
    })
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to handle onlay overlay:', error)
    }
  }
}

/**
 * Handles periradicular overlay with severity levels
 * @param svg - The root SVG element
 * @param periradicularSeverity - Severity level (slight, moderate, severe)
 */
export function handlePeriradicularOverlay(
  svg: SVGSVGElement,
  periradicularSeverity: string | undefined,
): void {
  try {
    const periradicularOverlay = getOverlayElement(
      svg,
      '.periradicular-overlay',
    )
    if (!periradicularOverlay) {
      return
    }

    // Hide all child severity levels first
    periradicularOverlay
      .querySelectorAll(
        'g[class*="periradicular-overlay severe"], g[class*="periradicular-overlay moderate"], [data-layer-variant]',
      )
      .forEach((el) => {
        ;(el as SVGElement).style.display = DISPLAY.NONE
      })

    if (periradicularSeverity) {
      const severityVariant = getSeverityVariant(periradicularSeverity)
      const severityElement =
        periradicularOverlay.querySelector<SVGElement>(
          `.periradicular-overlay.${periradicularSeverity}`,
        ) || findVariantElement(periradicularOverlay, severityVariant)
      if (severityElement) {
        severityElement.style.display = DISPLAY.BLOCK
        toggleOverlay(periradicularOverlay, true)
      }
    } else {
      toggleOverlay(periradicularOverlay, false)
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to handle periradicular overlay:', error)
    }
  }
}

/**
 * Handles attrition/abrasion overlay with severity levels
 * @param svg - The root SVG element
 * @param overlaySelector - CSS selector for the overlay
 * @param severity - Severity level (slight, moderate, severe)
 */
export function handleSeverityOverlay(
  svg: SVGSVGElement,
  overlaySelector: string,
  severity: string | undefined,
): void {
  try {
    const overlay = getOverlayElement(svg, overlaySelector)
    if (!overlay) {
      return
    }

    // Hide all severity overlays first
    overlay
      .querySelectorAll(`${overlaySelector}, [data-layer-variant]`)
      .forEach((el) => {
        ;(el as SVGElement).style.display = DISPLAY.NONE
      })

    if (severity) {
      const severityVariant = getSeverityVariant(severity)
      const severityElement =
        overlay.querySelector<SVGElement>(`${overlaySelector}.${severity}`) ||
        findVariantElement(overlay, severityVariant)
      if (severityElement) {
        toggleOverlay(overlay, true)
        severityElement.style.display = DISPLAY.BLOCK
      }
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `Failed to handle severity overlay ${overlaySelector}:`,
        error,
      )
    }
  }
}
